import { type QueryKey } from '@tanstack/react-query';
import { memo, type ReactElement, useMemo, useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { buildJobRunDetailPath } from '@/features/jobs/utils/jobPaths';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import {
  useDeletePipelineStage,
  useUpdatePipelineStage,
} from '@/features/pipelines/hooks/pipelines';
import { toPipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import {
  buildStageDetailPath,
  buildStageExecutionDetailPath,
} from '@/features/pipelines/utils/stagePaths';
import { mapStageExecutionSummaryItems } from '@/features/pipelines/utils/stageSummaryLinks';
import {
  useProjectFilter,
  useProjectMetricThresholds,
  useProjectsQuery,
} from '@/features/projects';
import { DetailEditEntityDialog } from '@/features/shared/components/entityDetails/DetailEditEntityDialog';
import { getBuildStatusIconComponent } from '@/features/shared/components/tableSystem/buildStatusIcons';
import { TableBuildStatusBreakdownCell } from '@/features/shared/components/tableSystem/TableBuildStatusBreakdownCell';
import {
  TableActionsCell,
  TableCellContainer,
} from '@/features/shared/components/tableSystem/TableCells';
import {
  TABLE_ROW_HEIGHT,
  TABLE_ROW_MEASURE_HEIGHT,
} from '@/features/shared/components/tableSystem/tableConstants';
import {
  resolveLatestChipBackground,
  resolveLatestChipForeground,
} from '@/features/shared/components/tableSystem/TableLatestCellStyles';
import {
  CLONE_META_DETAIL_GLYPH_SX,
  TableStatusMetaContent,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import { TableMetricsBreakdownCell } from '@/features/shared/components/tableSystem/TableMetricsBreakdownCell';
import { TableStageStatusBreakdownCell } from '@/features/shared/components/tableSystem/TableStageStatusBreakdownCell';
import { tableSx } from '@/features/shared/components/tableSystem/tableSx';
import { useDetailEditEditor } from '@/features/shared/hooks/useDetailEditEditor';
import { buildTagColorByName, resolveTagColor } from '@/features/shared/tags';
import {
  resolveCiStatusLabelKey,
  resolveStageDisplayStatus,
} from '@/features/shared/utils/ciStatus';
import { mapChipKeyToStatusTone } from '@/features/shared/utils/tableMetricPresentation';
import { useNotifications } from '@/providers/notificationHooks';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getChartTitle } from '@/shared/copy/charts';
import { getDetailLabel } from '@/shared/copy/details';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { getStatusCopy } from '@/shared/copy/status';
import { getToastMessage } from '@/shared/copy/toasts';
import { useTagOptions } from '@/shared/hooks';
import { Box, formatDuration, formatFrequency, formatPercent, formatStatusLabel } from '@/ui';
import { buildLocalQueryResult } from '@/ui/composites/grid/localGridUtils';
import { buildTableGridProps } from '@/ui/composites/grid/tableGridProps';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { TableNameCell, getThresholdChipKey } from '@/ui/composites/table';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import {
  StagesIcon as JobsIcon,
  resolveStatusChipTone,
  type GridColDef,
  type GridColumnVisibilityModel,
} from '@/ui/primitives';
import { EventOutlinedIcon, TimerOutlinedIcon } from '@/ui/primitives/icons';
import type { TagColor } from '@/shared/data/tags';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { SummaryTooltipItem } from '@/ui/composites/table/tableUtils';
import type {
  MetricThresholdGroup,
  PipelineStageListItemDto,
  QueryResult,
  QueryResult as GridQueryResult,
} from '@flowqa/shared';

export type StageSummaryRow = PipelineStageListItemDto;

type StagesTableProps = {
  rows?: StageSummaryRow[];
  noRowsText?: string;
  queryKey: QueryKey;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<StageSummaryRow>>;
  initialData?: GridQueryResult<StageSummaryRow>;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  search?: string;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (nextModel: GridColumnVisibilityModel) => void;
  preservePreviousData?: boolean;
};

const columnFlex = {
  stage: 0.9,
  latest: 0.55,
  latestJobs: 0.55,
  executions: 0.55,
  metrics: 0.9,
  actions: 0.1,
};

const mapLatestStageJobSummaryItems = (row: StageSummaryRow): SummaryTooltipItem[] =>
  (row.latestJobSummaries ?? []).map((job) => ({
    key: job.id,
    detailLabel: row.stageName,
    executionLabel: job.buildKey,
    href: buildStageDetailPath(row.id),
    executionHref:
      row.pipelineId && row.stageName && job.jobName
        ? buildJobRunDetailPath({
            jobRunId: job.id,
            pipelineId: row.pipelineId,
            stageName: row.stageName,
            jobName: job.jobName,
          })
        : buildStageExecutionDetailPath({
            stageExecutionId: row.latestRun?.id ?? row.id,
            pipelineId: row.pipelineId,
            stageName: row.stageName,
          }),
    runNumber: null,
    status: job.status,
    timestamp: job.timestamp,
  }));

const filterStageRowsBySearch = (
  rows: StageSummaryRow[],
  search: string | undefined,
): StageSummaryRow[] => {
  const normalizedSearch = search?.trim().toLowerCase();
  if (!normalizedSearch) {
    return rows;
  }

  return rows.filter((row) =>
    [row.pipelineName ?? '', row.stageName, row.status, ...(row.tags ?? [])].some((value) =>
      value.toLowerCase().includes(normalizedSearch),
    ),
  );
};

type BuildStageColumnsOptions = {
  selectedProjectId: string | null;
  thresholds: MetricThresholdGroup;
  resolveThresholds: (row: { projectId: string }) => MetricThresholdGroup | undefined;
  tagColorByName?: Partial<Record<string, TagColor>>;
  includeActionsColumn?: boolean;
  onEditRow?: (row: StageSummaryRow) => void;
  onDeleteRow?: (row: StageSummaryRow) => void;
  editRowPendingId?: string | null;
  deleteRowPendingId?: string | null;
};

const buildStagesColumns = ({
  selectedProjectId,
  thresholds,
  resolveThresholds,
  tagColorByName = {},
  includeActionsColumn = false,
  onEditRow,
  onDeleteRow,
  editRowPendingId = null,
  deleteRowPendingId = null,
}: BuildStageColumnsOptions): Array<GridColDef<StageSummaryRow>> => {
  const resolvedThresholds = (row: StageSummaryRow): MetricThresholdGroup => {
    if (selectedProjectId) {
      return thresholds;
    }

    return resolveThresholds(row) ?? thresholds;
  };

  const stageColumn: GridColDef<StageSummaryRow> = {
    field: 'stageName',
    headerName: getDetailLabel('stage'),
    flex: columnFlex.stage,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-name-cell',
    renderCell: (params): ReactElement => {
      const pipelineSlug =
        params.row.pipelineName && params.row.pipelineName.length > 0
          ? toPipelineSlug(params.row.pipelineName)
          : null;
      const pipelineSegment =
        pipelineSlug && pipelineSlug.length > 0 ? pipelineSlug : (params.row.pipelineId ?? null);

      return (
        <TableCellContainer gap="none">
          <TableNameCell
            primary={{
              label: params.row.stageName,
              href: buildStageDetailPath(params.row.id),
            }}
            secondary={
              params.row.pipelineName
                ? {
                    label: params.row.pipelineName,
                    href: pipelineSegment ? `/pipelines/${pipelineSegment}` : '/pipelines',
                  }
                : null
            }
            tags={(params.row.tags ?? []).map((tag) => ({
              label: tag,
              tone: resolveTagColor(tag, tagColorByName),
            }))}
          />
        </TableCellContainer>
      );
    },
  };

  const latestStatusColumn: GridColDef<StageSummaryRow> = {
    field: 'status',
    headerName: getChartTitle('latestStatus'),
    flex: columnFlex.latest,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-status-meta-cell',
    filterable: false,
    sortable: false,
    renderCell: (params): ReactElement => {
      const latestRun = params.row.latestRun ?? null;
      const status =
        resolveStageDisplayStatus(params.row) ?? latestRun?.status ?? params.row.status;
      const statusTone = resolveStatusChipTone(status);
      const StatusIcon = getBuildStatusIconComponent(status);
      const durationLabel = formatDuration(latestRun?.durationMs ?? null);
      const timestamp = latestRun?.timestamp ?? params.row.lastUpdatedAt ?? null;
      const timestampLabel = timestamp ? formatRelativeTime(timestamp) : '';
      const timestampTitle = timestamp ? formatDate(timestamp, { includeTime: true }) : '';
      const runHref = latestRun
        ? buildStageExecutionDetailPath({
            stageExecutionId: latestRun.id,
            pipelineId: params.row.pipelineId,
            stageName: params.row.stageName,
          })
        : null;

      return (
        <TableCellContainer gap="none">
          <TableStatusMetaContent
            statusLabel={formatStatusLabel(resolveCiStatusLabelKey(status))}
            statusHref={runHref}
            chipTone={statusTone}
            chipIcon={<Box component={StatusIcon} />}
            chipBackgroundColor={(theme) => resolveLatestChipBackground(theme, statusTone)}
            chipForegroundColor={(theme) => resolveLatestChipForeground(theme, statusTone)}
            durationLabel={durationLabel}
            durationIcon={<TimerOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
            timestampLabel={timestampLabel}
            timestampTitle={timestampTitle}
            timestampIcon={<EventOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
          />
        </TableCellContainer>
      );
    },
  };

  const executionsColumn: GridColDef<StageSummaryRow> = {
    field: 'executionStatusCounts',
    headerName: 'Runs',
    flex: columnFlex.executions,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-breakdown-cell',
    filterable: false,
    sortable: false,
    renderCell: (params): ReactElement => (
      <TableCellContainer gap="none">
        <TableStageStatusBreakdownCell
          total={params.row.executionTotal}
          counts={params.row.executionStatusCounts}
          summaryItems={mapStageExecutionSummaryItems(params.row)}
          totalLabelSingular="run"
          totalLabelPlural="runs"
          showDetailLabel={false}
        />
      </TableCellContainer>
    ),
  };

  const latestJobsColumn: GridColDef<StageSummaryRow> = {
    field: 'latestJobStatusCounts',
    headerName: 'Latest Jobs',
    flex: columnFlex.latestJobs,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-breakdown-cell',
    filterable: false,
    sortable: false,
    renderCell: (params): ReactElement => (
      <TableCellContainer gap="none">
        <TableBuildStatusBreakdownCell
          total={params.row.latestJobTotal ?? 0}
          counts={params.row.latestJobStatusCounts}
          summaryItems={mapLatestStageJobSummaryItems(params.row)}
          totalIcon={JobsIcon}
          showDateHeaders={false}
        />
      </TableCellContainer>
    ),
  };

  const metricsColumn: GridColDef<StageSummaryRow> = {
    field: 'metricsSummary',
    headerName: getDetailLabel('metrics'),
    flex: columnFlex.metrics,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-breakdown-cell',
    filterable: false,
    sortable: false,
    renderCell: (params): ReactElement => {
      const passRate = params.row.metrics?.passRate ?? null;
      const frequency = params.row.metrics?.frequencyPerDay ?? null;
      const flakeScore = params.row.metrics?.flakeScore ?? null;
      const avgDuration = params.row.metrics?.averageDurationMs ?? null;
      const avgDurationThresholdValue = avgDuration === null ? null : avgDuration / 1000;
      const thresholdGroup = resolvedThresholds(params.row);

      return (
        <TableCellContainer gap="none">
          <TableMetricsBreakdownCell
            items={[
              {
                key: 'passRate',
                label: getChartTitle('passRate'),
                valueLabel:
                  passRate === null ? getStatusCopy('noData') : `${formatPercent(passRate)}%`,
                deltaValue: null,
                isMissing: passRate === null,
                tone: mapChipKeyToStatusTone(
                  getThresholdChipKey(passRate, thresholdGroup.passRate),
                ),
                direction: thresholdGroup.passRate.direction,
              },
              {
                key: 'frequency',
                label: getChartTitle('frequency'),
                valueLabel:
                  frequency === null ? getStatusCopy('noData') : formatFrequency(frequency),
                deltaValue: null,
                isMissing: frequency === null,
                tone: mapChipKeyToStatusTone(
                  getThresholdChipKey(frequency, thresholdGroup.frequency),
                ),
                direction: thresholdGroup.frequency.direction,
              },
              {
                key: 'avgDuration',
                label: getChartTitle('avgDuration'),
                valueLabel:
                  avgDuration === null ? getStatusCopy('noData') : formatDuration(avgDuration),
                deltaValue: null,
                isMissing: avgDuration === null,
                tone: mapChipKeyToStatusTone(
                  getThresholdChipKey(avgDurationThresholdValue, thresholdGroup.avgDuration),
                ),
                direction: thresholdGroup.avgDuration.direction,
              },
              {
                key: 'flakeScore',
                label: getChartTitle('flakeScore'),
                valueLabel:
                  flakeScore === null ? getStatusCopy('noData') : `${formatPercent(flakeScore)}%`,
                deltaValue: null,
                isMissing: flakeScore === null,
                tone: mapChipKeyToStatusTone(
                  getThresholdChipKey(flakeScore, thresholdGroup.flakeScore),
                ),
                direction: thresholdGroup.flakeScore.direction,
              },
            ]}
          />
        </TableCellContainer>
      );
    },
  };

  const actionsColumn: GridColDef<StageSummaryRow> = {
    field: 'actions',
    headerName: '',
    flex: columnFlex.actions,
    minWidth: 56,
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params): ReactElement => (
      <TableActionsCell
        rowName={params.row.stageName}
        editActionLabel={getEntityActionLabel('edit', 'Stage')}
        deleteActionLabel={getEntityActionLabel('delete', 'Stage')}
        onEdit={() => {
          onEditRow?.(params.row);
        }}
        onDelete={() => {
          onDeleteRow?.(params.row);
        }}
        disableEdit={editRowPendingId === params.row.id || deleteRowPendingId === params.row.id}
        disableDelete={deleteRowPendingId === params.row.id}
      />
    ),
  };

  return [
    stageColumn,
    latestStatusColumn,
    latestJobsColumn,
    executionsColumn,
    metricsColumn,
    ...(includeActionsColumn ? [actionsColumn] : []),
  ];
};

const StagesTableComponent = ({
  rows = [],
  noRowsText = getEmptyStateMessage('entity.noneFound', { entity: 'stage' }),
  queryKey,
  queryFn,
  initialData,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize = 5,
  search,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  preservePreviousData = false,
}: StagesTableProps): ReactElement => {
  const { session } = useAuth();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const { notify } = useNotifications();
  const { tagOptions } = useTagOptions({ projectId: selectedProjectId });
  const updateStage = useUpdatePipelineStage();
  const deleteStage = useDeletePipelineStage();
  const projectsQuery = useProjectsQuery();
  const [editTarget, setEditTarget] = useState<StageSummaryRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<StageSummaryRow | null>(null);
  const editEditor = useDetailEditEditor({
    projectId: editTarget?.projectId ?? null,
    successMessage: 'Stage updated',
    saveEntity: async ({ name }) => {
      if (!editTarget || !name) {
        return;
      }

      await updateStage.mutateAsync({
        id: editTarget.id,
        stageName: name,
        projectId: editTarget.projectId,
      });
    },
  });
  const thresholdsByProjectId = useMemo(() => {
    const entries = projectsQuery.data?.items ?? [];
    return new Map(entries.map((project) => [project.id, project.metricThresholds.stages]));
  }, [projectsQuery.data?.items]);
  const resolveThresholds = useMemo(
    () =>
      (row: { projectId: string }): MetricThresholdGroup | undefined =>
        thresholdsByProjectId.get(row.projectId),
    [thresholdsByProjectId],
  );
  const tagColorByName = useMemo(() => buildTagColorByName(tagOptions), [tagOptions]);
  const columns = useMemo(
    () =>
      buildStagesColumns({
        selectedProjectId,
        thresholds: thresholds.stages,
        resolveThresholds,
        tagColorByName,
        includeActionsColumn: true,
        onEditRow: (row) => {
          setEditTarget(row);
        },
        onDeleteRow: (row) => {
          setDeleteTarget(row);
        },
        editRowPendingId: editEditor.isPending ? (editTarget?.id ?? null) : null,
        deleteRowPendingId: deleteStage.isPending ? (deleteTarget?.id ?? null) : null,
      }),
    [
      deleteStage.isPending,
      deleteTarget?.id,
      editEditor.isPending,
      editTarget?.id,
      resolveThresholds,
      selectedProjectId,
      tagColorByName,
      thresholds.stages,
    ],
  );
  const togglableColumnFields = useMemo(() => columns.map((column) => column.field), [columns]);

  const localQueryFn = useMemo(
    () =>
      queryFn ??
      (async (input: GridQueryInput): Promise<QueryResult<StageSummaryRow>> => {
        const filteredRows = filterStageRowsBySearch(rows, search);
        return await Promise.resolve(buildLocalQueryResult(filteredRows, input));
      }),
    [queryFn, rows, search],
  );

  const gridProps = buildTableGridProps<StageSummaryRow>({
    queryFn: localQueryFn,
    queryKey,
    pageSizeOptions,
    initialPageSize,
    noRowsText,
  });

  return (
    <>
      <ServerDataTable<StageSummaryRow>
        queryPath="stages/query"
        initialData={initialData}
        queryFn={gridProps.queryFn}
        queryKey={gridProps.queryKey}
        columns={columns}
        getRowId={(row) => row.id}
        isAuthenticated={Boolean(session?.userId)}
        organisationId={selectedOrganisationId}
        projectId={selectedProjectId}
        pageSizeOptions={gridProps.pageSizeOptions}
        initialPageSize={gridProps.initialPageSize}
        noRowsText={gridProps.noRowsText}
        interactionMode="static"
        getRowHeight={() => 'auto'}
        skeletonRowHeight={TABLE_ROW_HEIGHT}
        skeletonGetRowHeight={null}
        showSkeletonOnFetch={preservePreviousData}
        showPaginationSkeletonWhenRowsVisible={preservePreviousData}
        preservePreviousData={preservePreviousData}
        autoHeight
        showToolbar={false}
        showColumnVerticalBorder={false}
        showCellVerticalBorder={false}
        gridProps={{
          getEstimatedRowHeight: () => TABLE_ROW_MEASURE_HEIGHT,
          onColumnVisibilityModelChange,
          slotProps: {
            columnsManagement: {
              disableShowHideToggle: true,
              disableResetButton: true,
              getTogglableColumns: () => togglableColumnFields,
            },
          },
        }}
        columnVisibilityModel={columnVisibilityModel}
        sx={tableSx}
      />
      <DetailEditEntityDialog
        open={editTarget !== null}
        entityLabel="Stage"
        entityName={editTarget?.stageName ?? ''}
        nameValue={editTarget?.stageName ?? ''}
        projectId={editTarget?.projectId ?? null}
        loading={editEditor.isPending}
        cascadeCopy="Renaming this stage updates matching stage executions for the same pipeline."
        onClose={() => {
          if (editEditor.isPending) {
            return;
          }
          setEditTarget(null);
        }}
        onSave={async ({ name }) => {
          await editEditor.save({ name });
          setEditTarget(null);
        }}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'stage' })}
        description="This stage will be deleted from the stage rollup view."
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', { entity: 'stage' })}
        confirmColor="red"
        loading={deleteStage.isPending}
        onClose={() => {
          if (deleteStage.isPending) {
            return;
          }
          setDeleteTarget(null);
        }}
        onConfirm={() => {
          if (!deleteTarget) {
            return;
          }

          deleteStage.mutate(
            { id: deleteTarget.id, projectId: deleteTarget.projectId },
            {
              onSuccess: () => {
                notify({
                  message: getToastMessage('entity.deleted', {
                    entity: 'stage',
                    name: deleteTarget.stageName,
                  }),
                  tone: 'green',
                });
                setDeleteTarget(null);
              },
            },
          );
        }}
      />
    </>
  );
};

export const StagesTable = memo(StagesTableComponent);
